"use client"

import { Head, router, usePage } from "@inertiajs/react"
import { SearchLg } from "@untitledui/icons"
import { AnimatePresence, motion } from "motion/react"
import { useMemo, useState } from "react"

import { PlaylistCard } from "@/components/playlists"
import { HighlighterStroke } from "@/components/ui/highlighter-stroke"
import { Input } from "@/components/ui/input"
import { PaginationPageMinimalCenter } from "@/components/ui/pagination"
import { AuthedShell } from "@/layouts/authed-shell"
import { getPaginationUrl } from "@/lib/navigations"
import { playlistsPath } from "@/routes"
import type { Playlist, SharedData } from "@/types"
import type { Pagination } from "@/types/contents/pagination"

interface PlaylistsIndexProps {
  playlists: Playlist[]
  pagination: Pagination
}

export default function PlaylistsIndex({
  playlists,
  pagination,
}: PlaylistsIndexProps) {
  const { visitor } = usePage<SharedData>().props
  const [query, setQuery] = useState("")

  /*
   * The filteredPlaylists() function manages playlists to be displayed.
   * Returns playlists filtered by the search query if it exists.
   * Otherwise, falls back to the complete list provided via props.
   */
  const filteredPlaylists = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    if (!keyword) return playlists
    return playlists.filter((playlist) =>
      playlist.title.toLowerCase().includes(keyword),
    )
  }, [playlists, query])

  return (
    <AuthedShell headerVariant="light">
      <Head title="Playlists" />

      {/* Inverted navy masthead: mirrors the briefs index so discovery opens
          like a print masthead — type, Scotch rule, and search redrawn in
          paper white against the navy ground. */}
      <section className="bg-surface-strong border-b border-white/15 py-16 lg:py-24">
        <div className="max-w-container mx-auto flex flex-col gap-5 px-4 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-2">
              <span
                aria-hidden="true"
                className="scotch-rule scotch-rule-action mb-2"
              />
              <h1 className="text-primary_on-brand text-display-sm lg:text-display-md font-semibold text-balance">
                Discover Playlists
              </h1>
              <p className="text-secondary_on-brand text-lg text-pretty">
                Browse curated playlists from the community.{" "}
                <HighlighterStroke onStrong>Tap any cover</HighlighterStroke> to
                drop straight into the player.
              </p>
            </div>
            <div className="w-full lg:max-w-80">
              <Input
                size="sm"
                placeholder="Search playlists..."
                value={query}
                onChange={setQuery}
                icon={SearchLg}
                className="min-w-xs"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Playlist grid */}
      <div className="max-w-container mx-auto flex min-h-[calc(80vh)] w-full flex-1 flex-col gap-6 px-4 py-10 lg:px-8 lg:py-12">
        {filteredPlaylists.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {/* Animation for filtered-out cards away smoothly to their new grid positions. */}
              <AnimatePresence mode="popLayout" initial={false}>
                {filteredPlaylists.map((playlist) => (
                  <motion.div
                    key={playlist.id}
                    layout
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <PlaylistCard
                      playlist={playlist}
                      playOnClick
                      showAddToMyPlaylist={visitor.authenticated}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {!query && pagination && pagination.total > 1 && (
              <PaginationPageMinimalCenter
                page={pagination.page}
                total={pagination.total}
                className="mt-6"
                onPageChange={(page) =>
                  router.visit(getPaginationUrl(playlistsPath(), page))
                }
              />
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
            <p className="text-primary text-lg font-semibold">
              {query ? "No playlists match your search" : "No playlists yet"}
            </p>
            <p className="text-tertiary mt-1 text-sm">
              {query
                ? "Try a different keyword."
                : "Be the first to create a playlist!"}
            </p>
          </div>
        )}
      </div>
    </AuthedShell>
  )
}
